import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { getUiLang } from '../../utils/lang';
import { 
  Plus, Edit, Trash2, X, Save, ChevronRight, ChevronDown, 
  Folder, FolderOpen, Package, Loader2 
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCategories = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedParent, setSelectedParent] = useState(null);

  const languages = ['sk', 'en', 'de', 'cs', 'ru'];
  const languageNames = { sk: 'SK', en: 'EN', de: 'DE', cs: 'CS', ru: 'RU' };
  const [activeLang, setActiveLang] = useState('sk');
  const [showIconPicker, setShowIconPicker] = useState(false);

  // Available icons for categories
  const availableIcons = [
    { id: 'building', emoji: '🏢', label: 'Building' },
    { id: 'car', emoji: '🚗', label: 'Car' },
    { id: 'tools', emoji: '🔧', label: 'Tools' },
    { id: 'package', emoji: '📦', label: 'Package' },
    { id: 'sun', emoji: '🌞', label: 'Sun/Exterior' },
    { id: 'home', emoji: '🏠', label: 'Home/Interior' },
    { id: 'shield', emoji: '🛡️', label: 'Protection' },
    { id: 'lock', emoji: '🔒', label: 'Privacy' },
    { id: 'paint', emoji: '🎨', label: 'Anti-graffiti' },
    { id: 'uv', emoji: '☀️', label: 'UV Protection' },
    { id: 'strong', emoji: '💪', label: 'Impact resistant' },
    { id: 'eye', emoji: '👁️', label: 'Visibility' },
    { id: 'window', emoji: '🪟', label: 'Window' },
    { id: 'film', emoji: '🎞️', label: 'Film' },
    { id: 'sparkle', emoji: '✨', label: 'Premium' },
    { id: 'leaf', emoji: '🌿', label: 'Eco' },
  ];

  const emptyForm = {
    slug: '',
    parent_id: null,
    sort_order: 0,
    icon: '',
    image_url: '',
    translations: {
      sk: { name: '', description: '' },
      en: { name: '', description: '' },
      de: { name: '', description: '' },
      cs: { name: '', description: '' },
      ru: { name: '', description: '' }
    }
  };

  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
        // Auto-expand root categories
        const expanded = {};
        data.filter(c => !c.parent_id).forEach(c => expanded[c.id] = true);
        setExpandedCategories(expanded);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error(t('admin.categories_page.fetch_error', 'Chyba pri načítaní kategórií'));
    } finally {
      setLoading(false);
    }
  };

  const getTranslation = (cat, field) => {
    return cat?.translations?.[getUiLang(i18n)]?.[field] || 
           cat?.translations?.['sk']?.[field] || 
           cat?.translations?.['en']?.[field] || '';
  };

  const toggleExpand = (catId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [catId]: !prev[catId]
    }));
  };

  const getChildren = (parentId) => {
    return categories.filter(c => c.parent_id === parentId);
  };

  const handleCreate = (parentId = null) => {
    setEditingCategory(null);
    setSelectedParent(parentId);
    setFormData({
      ...emptyForm,
      parent_id: parentId
    });
    setShowForm(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setSelectedParent(category.parent_id);
    setFormData({
      slug: category.slug || '',
      parent_id: category.parent_id,
      sort_order: category.sort_order || 0,
      icon: category.icon || '',
      image_url: category.image_url || '',
      translations: category.translations || emptyForm.translations
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.translations.sk.name) {
      toast.error(t('admin.categories_page.name_required', 'Názov kategórie je povinný'));
      return;
    }

    setSaving(true);
    try {
      const url = editingCategory 
        ? `${API_URL}/api/admin/categories/${editingCategory.id}`
        : `${API_URL}/api/admin/categories`;
      
      const method = editingCategory ? 'PUT' : 'POST';
      
      // Generate slug from Slovak name if not provided
      const slug = formData.slug || formData.translations.sk.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          slug
        })
      });

      if (response.ok) {
        toast.success(editingCategory ? t('admin.categories_page.updated', 'Kategória aktualizovaná') : t('admin.categories_page.created', 'Kategória vytvorená'));
        await fetchCategories();
        setShowForm(false);
        setEditingCategory(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || t('admin.categories_page.save_error', 'Chyba pri ukladaní'));
      }
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(t('admin.categories_page.save_error', 'Chyba pri ukladaní'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (categoryId) => {
    const children = getChildren(categoryId);
    if (children.length > 0) {
      toast.error(t('admin.categories_page.remove_children_first', 'Najprv odstráňte podkategórie'));
      return;
    }

    if (!window.confirm(t('admin.categories_page.delete_confirm', 'Naozaj chcete odstrániť túto kategóriu?'))) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/categories/${categoryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(t('admin.categories_page.deleted', 'Kategória odstránená'));
        await fetchCategories();
      } else {
        toast.error(t('admin.categories_page.delete_error', 'Chyba pri odstraňovaní'));
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(t('admin.categories_page.delete_error', 'Chyba pri odstraňovaní'));
    }
  };

  const updateTranslation = (lang, field, value) => {
    setFormData(prev => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          [field]: value
        }
      }
    }));
  };

  const renderCategoryTree = (parentId = null, level = 0) => {
    const items = categories.filter(c => c.parent_id === parentId);
    
    if (items.length === 0 && level > 0) return null;

    return items.map(cat => {
      const children = getChildren(cat.id);
      const hasChildren = children.length > 0;
      const isExpanded = expandedCategories[cat.id];

      return (
        <div key={cat.id} className="select-none">
          <div 
            className={`flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-gray-50 group ${
              level === 0 ? 'bg-white shadow-sm mb-2' : 'ml-6 border-l-2 border-gray-200'
            }`}
          >
            {/* Expand/Collapse button */}
            <button
              onClick={() => toggleExpand(cat.id)}
              className={`p-1 rounded hover:bg-gray-200 ${!hasChildren && 'invisible'}`}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-500" />
              )}
            </button>

            {/* Icon - custom or default */}
            {cat.icon ? (
              <span className="text-xl">{availableIcons.find(i => i.id === cat.icon)?.emoji || '📁'}</span>
            ) : hasChildren ? (
              isExpanded ? <FolderOpen className="h-5 w-5 text-orange-500" /> : <Folder className="h-5 w-5 text-orange-400" />
            ) : (
              <Package className="h-4 w-4 text-gray-400" />
            )}

            {/* Name */}
            <span className={`flex-1 ${level === 0 ? 'font-semibold' : ''}`}>
              {getTranslation(cat, 'name')}
            </span>

            {/* Actions */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
              <button
                onClick={() => handleCreate(cat.id)}
                className="p-1.5 rounded hover:bg-green-100 text-green-600"
                title={t('admin.categories_page.add_subcategory', 'Pridať podkategóriu')}
              >
                <Plus className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleEdit(cat)}
                className="p-1.5 rounded hover:bg-blue-100 text-blue-600"
                title={t('admin.categories_page.edit', 'Upraviť')}
              >
                <Edit className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDelete(cat.id)}
                className="p-1.5 rounded hover:bg-red-100 text-red-600"
                title={t('admin.categories_page.delete_action', 'Odstrániť')}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Children */}
          {hasChildren && isExpanded && (
            <div className="ml-4">
              {renderCategoryTree(cat.id, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t('admin.categories_menu')}</h1>
        <Button onClick={() => handleCreate(null)} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="h-4 w-4 mr-2" />
          {t('admin.add_item')}
        </Button>
      </div>

      {/* Category Tree */}
      <div className="bg-gray-50 rounded-xl p-4">
        {categories.filter(c => !c.parent_id).length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {t('admin.categories_page.empty', 'Zatiaľ nie sú žiadne kategórie')}
          </div>
        ) : (
          renderCategoryTree()
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {editingCategory ? t('admin.categories_page.edit_title', 'Upraviť kategóriu') : t('admin.categories_page.new_title', 'Nová kategória')}
                {selectedParent && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({t('admin.categories_page.subcategory_of', 'podkategória')}: {getTranslation(categories.find(c => c.id === selectedParent), 'name')})
                  </span>
                )}
              </h2>
              <button onClick={() => setShowForm(false)}>
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Language Tabs */}
              <div className="flex gap-2">
                {languages.map(lang => (
                  <button
                    key={lang}
                    onClick={() => setActiveLang(lang)}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      activeLang === lang 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {languageNames[lang]}
                  </button>
                ))}
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('admin.categories_page.field_name', 'Názov')} ({languageNames[activeLang]}) *
                </label>
                <Input
                  value={formData.translations[activeLang]?.name || ''}
                  onChange={(e) => updateTranslation(activeLang, 'name', e.target.value)}
                  placeholder={t('admin.categories_page.name_ph', 'Názov kategórie')}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Popis ({languageNames[activeLang]})
                </label>
                <textarea
                  className="w-full border rounded-lg p-3 text-sm"
                  rows={3}
                  value={formData.translations[activeLang]?.description || ''}
                  onChange={(e) => updateTranslation(activeLang, 'description', e.target.value)}
                  placeholder={t('admin.categories_page.desc_ph', 'Popis kategórie')}
                />
              </div>

              {/* Sort Order */}
              <div>
                <label className="block text-sm font-medium mb-1">Poradie</label>
                <Input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">{t('admin.categories_page.sort_hint', 'Menšie číslo = vyššie poradie')}</p>
              </div>

              {/* Icon Picker */}
              <div>
                <label className="block text-sm font-medium mb-2">{t('admin.categories_page.icon_label', 'Ikona kategórie')}</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowIconPicker(!showIconPicker)}
                    className="w-full flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 transition"
                  >
                    {formData.icon ? (
                      <>
                        <span className="text-3xl">{availableIcons.find(i => i.id === formData.icon)?.emoji || '📁'}</span>
                        <span className="text-gray-600">{availableIcons.find(i => i.id === formData.icon)?.label}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-3xl text-gray-300">📁</span>
                        <span className="text-gray-400">Vyberte ikonu...</span>
                      </>
                    )}
                  </button>
                  
                  {showIconPicker && (
                    <div className="absolute z-10 top-full left-0 right-0 mt-2 bg-white border rounded-xl shadow-lg p-4">
                      <div className="grid grid-cols-4 gap-2">
                        {availableIcons.map(icon => (
                          <button
                            key={icon.id}
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, icon: icon.id }));
                              setShowIconPicker(false);
                            }}
                            className={`flex flex-col items-center gap-1 p-3 rounded-lg transition ${
                              formData.icon === icon.id 
                                ? 'bg-orange-100 ring-2 ring-orange-500' 
                                : 'hover:bg-gray-100'
                            }`}
                          >
                            <span className="text-2xl">{icon.emoji}</span>
                            <span className="text-xs text-gray-500">{icon.label}</span>
                          </button>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t">
                        <button
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, icon: '' }));
                            setShowIconPicker(false);
                          }}
                          className="text-sm text-gray-500 hover:text-gray-700"
                        >
                          {t('admin.categories_page.remove_icon', 'Odstrániť ikonu')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium mb-2">{t('admin.categories_page.image_label', 'Obrázok kategórie (URL)')}</label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                  placeholder="https://example.com/image.jpg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('admin.categories_page.image_hint', 'URL obrázka pre zobrazenie na stránke kategórií')}
                </p>
                {formData.image_url && (
                  <div className="mt-3 relative rounded-lg overflow-hidden h-32 bg-gray-100">
                    <img 
                      src={formData.image_url} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowForm(false)}>
                {t('common.cancel', 'Zrušiť')}
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                {t('common.save', 'Uložiť')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default AdminCategories;
